import { useState, useEffect, useCallback } from 'react';
import { Truck, Wrench, User, Building, Search, Plus, Pencil, Coffee } from 'lucide-react';
import { cn, getStatusColor, getStatusBg, API_BASE_URL, SOCKET_URL } from '../lib/utils';
import { useAppSelector } from '../store';
import { useActiveEvent } from '../contexts/EventContext';
import { io } from 'socket.io-client';
import CreateResourceModal from '../components/shared/CreateResourceModal';
import EditResourceModal from '../components/shared/EditResourceModal';
import BreakTimerDisplay from '../components/shared/BreakTimerDisplay';
import type { Resource, ResourceStatus, ResourceType } from '../types';

const typeIcons: Record<ResourceType, React.ElementType> = {
  vehicle: Truck,
  equipment: Wrench,
  personnel: User,
  facility: Building,
};

export default function ResourcesPage() {
  const { token } = useAppSelector((state) => state.auth);
  const { activeEventId } = useActiveEvent();
  const [resources, setResources] = useState<Resource[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ResourceStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<ResourceType | 'all'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);

  const fetchResources = useCallback(async () => {
    if (!token) return;
    try {
      const url = activeEventId
        ? `${API_BASE_URL}/resources?eventId=${activeEventId}`
        : `${API_BASE_URL}/resources`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setResources(data.resources || []);
      }
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    }
  }, [token, activeEventId]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  useEffect(() => {
    if (!token) return;
    const socket = io(SOCKET_URL, { transports: ['websocket'], auth: { token } });
    const refresh = () => { fetchResources(); };
    socket.on('resource:updated', refresh);
    socket.on('break:requested', refresh);
    socket.on('break:responded', refresh);
    socket.on('break:ended', refresh);
    return () => { socket.disconnect(); };
  }, [token, fetchResources]);

  const pendingBreakCount = resources.filter((r) => r.pendingBreakRequest).length;

  const filtered = resources.filter((r) => {
    if (search && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    return true;
  });

  const statusCounts = {
    available: resources.filter((r) => r.status === 'available').length,
    deployed: resources.filter((r) => r.status === 'deployed' || r.status === 'dispatched').length,
    en_route: resources.filter((r) => r.status === 'en_route').length,
    on_break: resources.filter((r) => r.status === 'on_break' || r.activeBreak).length,
    maintenance: resources.filter((r) => r.status === 'maintenance').length,
  };

  const handleResourceCreated = () => {
    fetchResources(); // Refresh resources from API
  };

  return (
    <div className="h-full flex flex-col gap-3">
      {/* Header + status chips - fixed */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-lg font-bold text-foreground">Resources</h1>
          <p className="text-xs text-muted-foreground">
            {resources.length} total
            {pendingBreakCount > 0 && (
              <span className="ml-2 text-purple-400 font-semibold">
                · {pendingBreakCount} break request{pendingBreakCount > 1 ? 's' : ''} pending
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors mr-2"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Resource
          </button>
          {Object.entries(statusCounts).map(([status, count]) => (
            <button
              key={status}
              onClick={() => setStatusFilter(statusFilter === status ? 'all' : status as ResourceStatus)}
              className={cn(
                'px-2 py-1 rounded-lg border transition-all text-center',
                statusFilter === status
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-card hover:border-primary/30'
              )}
            >
              <div className="flex items-center gap-1">
                <div className={cn('w-1.5 h-1.5 rounded-full', status === 'available' ? 'bg-green-400' : status === 'deployed' ? 'bg-blue-400' : status === 'en_route' ? 'bg-cyan-400' : status === 'on_break' ? 'bg-purple-400' : 'bg-orange-400')} />
                <span className="text-xs font-bold text-foreground">{count}</span>
                <span className="text-[10px] text-muted-foreground capitalize hidden sm:inline">{status.replace('_', ' ')}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Filters - fixed */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-card border border-border text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ResourceType | 'all')}
          className="px-2 py-1.5 rounded-lg bg-card border border-border text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="all">All Types</option>
          <option value="vehicle">Vehicles</option>
          <option value="equipment">Equipment</option>
          <option value="personnel">Personnel</option>
          <option value="facility">Facilities</option>
        </select>
      </div>

      {/* Scrollable Resource Grid */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
          {filtered.map((resource) => {
            const TypeIcon = typeIcons[resource.type] || Wrench;
            return (
              <div
                key={resource.id}
                onClick={() => setEditingResource(resource)}
                className={cn(
                  'glass-panel p-3 hover:border-primary/30 transition-colors cursor-pointer relative group',
                  resource.pendingBreakRequest && 'border-purple-500/40 ring-1 ring-purple-500/20',
                  resource.activeBreak && 'border-purple-400/30'
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', getStatusBg(resource.status))}>
                      <TypeIcon className={cn('w-3.5 h-3.5', getStatusColor(resource.status))} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground">{resource.name}</p>
                      <p className="text-[10px] text-muted-foreground">{resource.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-medium border', getStatusBg(resource.status), getStatusColor(resource.status))}>
                      {resource.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                {resource.pendingBreakRequest && (
                  <div className="mb-2 px-2 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/25 space-y-1">
                    <div className="flex items-center gap-2">
                      <Coffee className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold text-purple-300">Break requested — {resource.pendingBreakRequest.typeLabel}</p>
                        <p className="text-[9px] text-muted-foreground truncate">{resource.pendingBreakRequest.userName}</p>
                      </div>
                    </div>
                    {resource.pendingBreakRequest.notes && (
                      <p className="text-[9px] text-muted-foreground pl-5 italic">{resource.pendingBreakRequest.notes}</p>
                    )}
                  </div>
                )}
                {resource.activeBreak && !resource.pendingBreakRequest && (
                  <div className="mb-2 px-2 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Coffee className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                        <p className="text-[10px] font-bold text-purple-300 truncate">
                          On break — {resource.activeBreak.typeLabel}
                        </p>
                      </div>
                      <BreakTimerDisplay activeBreak={resource.activeBreak} compact />
                    </div>
                    {resource.activeBreak.commandNotes && (
                      <p className="text-[9px] text-muted-foreground pl-5">{resource.activeBreak.commandNotes}</p>
                    )}
                    <p className="text-[9px] text-muted-foreground pl-5">
                      {resource.activeBreak.timerMode === 'countdown' ? 'Countdown' : 'Elapsed time'}
                    </p>
                  </div>
                )}
                <div className="space-y-1 text-[10px]">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Dept</span>
                    <span className="text-foreground">{resource.department}</span>
                  </div>
                  {resource.assignedPersonnel && resource.assignedPersonnel.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Personnel</span>
                      <span className="text-foreground text-right">{resource.assignedPersonnel.join(', ')}</span>
                    </div>
                  )}
                  {resource.assignedIncident && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Assigned</span>
                      <span className="text-primary font-mono">{resource.assignedIncident}</span>
                    </div>
                  )}
                  {resource.capabilities.length > 0 && (
                    <div className="flex flex-wrap gap-0.5 mt-1">
                      {resource.capabilities.map((cap) => (
                        <span key={cap} className="text-[9px] px-1 py-0.5 rounded bg-accent text-muted-foreground">
                          {cap}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Create Resource Modal */}
      <CreateResourceModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleResourceCreated}
        eventId={activeEventId || undefined}
      />
      {/* Edit Resource Modal */}
      <EditResourceModal
        open={!!editingResource}
        resource={editingResource}
        onClose={() => setEditingResource(null)}
        onSaved={() => fetchResources()}
        onDeleted={() => fetchResources()}
      />
    </div>
  );
}
